import { useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Select,
  Option,
  FormControl,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionGroup,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/joy";
import { api, downloadApiBlob } from "../../lib/api";
import { useOrganizationUsers } from "../../hooks/useOrganizationUsers";
import { SigningOrderManager } from "../../components/crm/contracts/SigningOrderManager";
import { PdfViewerModal } from "../../components/crm/contracts/PdfViewerModal";
import {
  SendContractModal,
  type SendContractData,
} from "../../components/crm/contracts/SendContractModal";
import {
  invitationResults,
  invitationSummary,
  type InvitationResult,
  type ContractWorkflow,
} from "../../components/crm/contracts/contractWorkflow";
import { ContractRecipientsEditor } from "../../components/crm/contracts/ContractRecipientsEditor";
import type { Contract } from "../../types";

export const ContractDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { users, loading: usersLoading, error: usersError } = useOrganizationUsers();
  const [preview, setPreview] = useState(false),
    [sendOpen, setSendOpen] = useState(false),
    [busy, setBusy] = useState(""),
    [error, setError] = useState("");
  const [management, setManagement] = useState<"delete" | "archive" | "unarchive" | null>(null);
  const [managementError, setManagementError] = useState("");
  const [recipientsDirty, setRecipientsDirty] = useState(false);
  const [results, setResults] = useState<{
    id: string;
    rows: InvitationResult[];
  } | null>(null);
  const revisionKey = useRef({ id, key: crypto.randomUUID() });
  if (revisionKey.current.id !== id)
    revisionKey.current = { id, key: crypto.randomUUID() };
  const query = useQuery({
    queryKey: ["contract-detail", id],
    queryFn: async () =>
      (await api.get<Contract>(`/api/crm/contracts/${id}/`)).data,
  });
  const workflow = useQuery({
    queryKey: ["contract-workflow", id],
    queryFn: async () =>
      (await api.get<ContractWorkflow>(`/api/crm/contracts/${id}/workflow/`))
        .data,
  });
  const contract = query.data;
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["contract-detail", id] }),
      client.invalidateQueries({ queryKey: ["contract-workflow", id] }),
      client.invalidateQueries({ queryKey: ["contract-design", id] }),
    ]);
  };
  const action = async (name: string, fn: () => Promise<unknown>) => {
    setBusy(name);
    setError("");
    try {
      await fn();
    } catch (e: any) {
      setError(
        e?.responseData?.error ||
          e.message ||
          "Action failed. Please try again.",
      );
    } finally {
      setBusy("");
    }
  };
  const send = async (data: SendContractData) => {
    try {
      const response = await api.post(
        `/api/crm/contracts/${contract!.id}/send/`,
        data,
      );
      setResults({ id: id!, rows: invitationResults(response.data) });
      setSendOpen(false);
      await refresh();
    } catch (e: any) {
      const rows = invitationResults(e.responseData);
      if (rows.length) setResults({ id: id!, rows });
      await refresh();
      throw Error(
        e?.responseData?.error ||
          "Invitation result is not confirmed. Review the timeline before retrying.",
      );
    }
  };
  if (query.isPending)
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress />
      </Box>
    );
  if (query.isError || !contract || String(contract.id) !== id)
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">Contract unavailable.</Alert>
        <Button onClick={() => query.refetch()}>Retry</Button>
        <Button component={Link} to="/crm/contracts">
          Back to contracts
        </Button>
      </Box>
    );
  const draft = contract.status === "draft";
  const data = workflow.data;
  const pdf =
    contract.completed_pdf_url || contract.signed_pdf_url || contract.pdf_url;
  const assignedRep = typeof contract.sales_rep === "number"
    ? contract.sales_rep : contract.sales_rep?.id ?? contract.sales_rep_id ?? null;
  const fieldProblems = data?.readiness.fields.filter(field => field.issues.length) || [];
  const missingRoles = [...new Set(fieldProblems.filter(field =>
    field.issues.includes("Required field has no assigned recipient")
  ).map(field => field.signer))];
  const roleLabel = (role: string) => role.replaceAll("_", " ");
  const currentResults = results?.id === id ? results.rows : [];
  return (
    <Box key={id} sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1500, mx: "auto" }}>
      <Button component={Link} to="/crm/contracts" variant="plain">
        Back to contracts
      </Button>
      <Stack
        direction="row"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
        sx={{ my: 2 }}
      >
        <Box>
          <Typography level="h2">{contract.title}</Typography>
          <Typography>{contract.contract_number}</Typography>
          <Chip color={contract.status === "completed" ? "success" : "neutral"}>
            {contract.status}
          </Chip>
        </Box>
        <Typography level="h3">
          {Number(contract.total_value || 0).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </Typography>
      </Stack>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!draft && (
        <Alert sx={{ mb: 2 }}>
          Document, recipients, and signing fields are fixed after sending.
          Create a draft revision for changes.
        </Alert>
      )}
      {currentResults.length > 0 && (
        <Alert
          color={currentResults.some((r) => !r.sent) ? "warning" : "success"}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography>{invitationSummary(currentResults)}</Typography>
            {currentResults.map((r) => (
              <Typography key={r.email}>
                {r.email}:{" "}
                {r.sent
                  ? "Provider accepted"
                  : "Failed — use the recipient action below"}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}
      {draft && (
        <Card sx={{ mb: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2} justifyContent="space-between" alignItems={{ sm: "center" }}>
            <Box>
              <Typography level="title-lg">Prepare for e-signature</Typography>
              <Typography level="body-sm">1. Review the document · 2. Confirm recipients · 3. Review and send</Typography>
            </Box>
            <Button disabled={!data?.readiness.ready || !!busy || recipientsDirty || workflow.isFetching} onClick={() => setSendOpen(true)}>
              Review and send
            </Button>
          </Stack>
          {recipientsDirty && <Alert color="warning">Save or cancel recipient changes below before continuing.</Alert>}
          {workflow.isError ? <Alert color="danger">Readiness could not be checked. <Button onClick={() => workflow.refetch()}>Retry</Button></Alert>
            : !data ? <Typography>Checking document and recipients…</Typography>
            : data.readiness.ready ? <Alert color="success">Ready to review. No invitation is sent until you confirm in the next step.</Alert>
            : <Alert color="warning"><Box>
                <Typography level="title-sm">Before you can send</Typography>
                {missingRoles.map(role => <Typography key={role}>Add a {roleLabel(role)} recipient below to complete their required fields.</Typography>)}
                {data.readiness.issues.map(issue => <Typography key={issue}>{issue}</Typography>)}
                {fieldProblems.some(field => field.issues.some(issue => issue !== "Required field has no assigned recipient")) &&
                  <Typography>Some signing fields need attention. Open Edit signing fields to correct them.</Typography>}
              </Box></Alert>}
        </Card>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0,2fr) minmax(300px,1fr)",
          },
          gap: 2,
        }}
      >
        <Stack gap={2}>
          <Card>
            <Typography level="title-lg">Contract document</Typography>
            <Typography>
              {contract.completed_pdf_url
                ? "Completed PDF with recorded signatures"
                : contract.status === "completed"
                  ? "Final PDF unavailable; the original remains available"
                  : "Original document preview"}{" "}
              · {contract.pdf_pages?.length || 0} {contract.pdf_pages?.length === 1 ? "page" : "pages"}
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Button disabled={!pdf} onClick={() => setPreview(true)}>
                Review document
              </Button>
              <Button
                disabled={!pdf || !!busy}
                variant="outlined"
                onClick={() =>
                  action("download", () =>
                    downloadApiBlob(pdf!, `${contract.contract_number}.pdf`),
                  )
                }
              >
                Download PDF
              </Button>
              {data?.final_pdf_available && contract.status === "completed" && (
                <Button
                  disabled={!!busy}
                  onClick={() =>
                    action("package", () =>
                      downloadApiBlob(
                        `/api/crm/contracts/${contract.id}/completed-package/`,
                        `${contract.contract_number}-completed.zip`,
                      ),
                    )
                  }
                >
                  Completed package
                </Button>
              )}
              {draft ? (
                <Button
                  component={Link}
                  to={`/crm/contracts/${contract.id}/design`}
                  variant="outlined"
                >
                  Edit signing fields
                </Button>
              ) : (
                <Button
                  disabled={!!busy}
                  onClick={() =>
                    action("revision", async () => {
                      const r = await api.post<{ id: number }>(
                        `/api/crm/contracts/${contract.id}/revise/`,
                        { request_id: revisionKey.current.key },
                      );
                      navigate(`/crm/contracts/${r.data.id}/design`);
                    })
                  }
                  variant="outlined"
                >
                  Create draft revision
                </Button>
              )}
            </Stack>
            {data?.final_pdf_available && (
              <Typography level="body-sm">
                The package includes the final PDF and available recorded
                signing evidence.
              </Typography>
            )}
          </Card>
          {draft && <ContractRecipientsEditor key={`${id}-${contract.updated_at}`} contract={contract} onSaved={refresh} onDirtyChange={setRecipientsDirty} />}
          <Card>
            <Typography level="title-lg">Customer and internal owner</Typography>
            {(contract as any).source_version_number && (
              <Typography>
                Source: {(contract as any).source_template_name} · Version{" "}
                {(contract as any).source_version_number}
              </Typography>
            )}
            <Typography
              component={Link}
              to={`/crm/contacts/${contract.customer}`}
            >
              {contract.customer_name}
            </Typography>
            <Typography>{contract.customer_email}</Typography>
            {contract.opportunity && (
              <Typography
                component={Link}
                to={`/crm/opportunities/${contract.opportunity}`}
              >
                {contract.opportunity_name || "Open opportunity"}
              </Typography>
            )}
            {data?.jobs.map((job) => (
              <Typography key={job.id} component={Link} to={`/jobs/${job.id}`}>
                {job.name}
              </Typography>
            ))}
            <FormControl>
              <FormLabel>Internal salesperson assignment</FormLabel>
              <Select
                value={assignedRep ? String(assignedRep) : "unassigned"}
                disabled={!!busy || usersLoading || !!usersError || recipientsDirty}
                onChange={(_, value) => {
                  // Joy can emit null while options register. Only the explicit
                  // Unassigned option may clear a saved assignment.
                  if (value === null) return;
                  void action("assignment", async () => {
                    const response = await api.patch<Contract>(`/api/crm/contracts/${contract.id}/`, {
                      sales_rep: value === "unassigned" ? null : Number(value),
                    });
                    client.setQueryData(["contract-detail", id], response.data);
                    await refresh();
                  });
                }}
              >
                <Option value="unassigned">Unassigned</Option>
                {assignedRep && !users.some(user => user.id === assignedRep) && (
                  <Option value={String(assignedRep)}>{contract.sales_rep_name || "Assigned salesperson"}</Option>
                )}
                {users.map((user) => (
                  <Option key={user.id} value={String(user.id)}>
                    {user.full_name || `${user.first_name} ${user.last_name}`}
                  </Option>
                ))}
              </Select>
              <Typography level="body-sm">Saved automatically. This is the internal owner; manage signing recipients separately above.</Typography>
              {usersError && <Alert color="warning">{usersError}. Your saved assignment is unchanged.</Alert>}
            </FormControl>
          </Card>
          {!draft && <Card>
            <Typography level="title-lg">Recipients and next steps</Typography>
            {recipientsDirty && <Alert color="warning">Save or cancel recipient changes below before continuing.</Alert>}
          {workflow.isError ? (
              <Alert color="danger">
                Recipient status unavailable.{" "}
                <Button onClick={() => workflow.refetch()}>Retry</Button>
              </Alert>
            ) : workflow.isPending ? (
              <CircularProgress />
            ) : (
              data?.recipients.map((r) => (
                <Box
                  key={r.email}
                  sx={{
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    overflowWrap: "anywhere",
                  }}
                >
                  <Stack
                    direction="row"
                    gap={1}
                    justifyContent="space-between"
                    flexWrap="wrap"
                  >
                    <Box>
                      <Typography level="title-md">
                        {r.name || r.email}
                      </Typography>
                      <Typography>
                        {r.email} · {r.role}
                      </Typography>
                      <Typography>
                        {r.next_step}
                        {r.signed_at
                          ? ` · ${new Date(r.signed_at).toLocaleString()}`
                          : ""}
                      </Typography>
                      {r.invitation && (
                        <Chip color={r.invitation.sent ? "success" : "warning"}>
                          {r.invitation.sent == null
                            ? "Invitation result unconfirmed"
                            : r.invitation.sent
                              ? "Provider accepted invitation"
                              : "Invitation failed"}
                        </Chip>
                      )}
                    </Box>
                    {r.can_remind && (
                      <Button
                        disabled={!!busy}
                        variant="outlined"
                        onClick={() =>
                          action(r.email, async () => {
                            const response = await api.post(
                              `/api/crm/contracts/${contract.id}/recipient-reminder/`,
                              { email: r.email },
                            );
                            setResults({
                              id: id!,
                              rows: invitationResults(response.data),
                            });
                            await refresh();
                          })
                        }
                      >
                        {r.invitation?.sent === false
                          ? "Retry invitation"
                          : "Send reminder"}
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))
            )}
          </Card>}
          <AccordionGroup><Accordion>
            <AccordionSummary>Activity timeline</AccordionSummary>
            <AccordionDetails>
            {data?.events_limited && (
              <Typography>
                Latest 200 events; the completed package includes all recorded
                events.
              </Typography>
            )}
            {data?.events.length === 0 && (
              <Typography>No recorded activity yet.</Typography>
            )}
            {data?.events.map((e) => (
              <Box key={e.id} sx={{ py: 1 }}>
                <Typography level="title-sm">{e.description}</Typography>
                <Typography level="body-sm">
                  {new Date(e.created_at).toLocaleString()}
                </Typography>
                {e.metadata.source_contract_id && (
                  <Typography
                    component={Link}
                    to={`/crm/contracts/${e.metadata.source_contract_id}`}
                  >
                    Original contract
                  </Typography>
                )}
                {e.metadata.revision_id && (
                  <Typography
                    component={Link}
                    to={`/crm/contracts/${e.metadata.revision_id}`}
                  >
                    Open draft revision
                  </Typography>
                )}
              </Box>
            ))}
            </AccordionDetails>
          </Accordion></AccordionGroup>
        </Stack>
        <Stack gap={2}>
          <Card>
            <Typography level="title-lg">Signing checklist</Typography>
            <Typography level="body-sm">{data?.readiness.fields.length || 0} fields · {data?.recipients.length || 0} recipients</Typography>
            <Typography level="body-sm">Required fields are completed by recipients when they sign. Before sending, each field needs a recipient and a valid position on the document.</Typography>
            <AccordionGroup><Accordion>
              <AccordionSummary>{fieldProblems.length ? `${fieldProblems.length} ${fieldProblems.length === 1 ? "field needs" : "fields need"} attention` : "View signing fields"}</AccordionSummary>
              <AccordionDetails>
                {(fieldProblems.length ? fieldProblems : data?.readiness.fields || []).map(f => (
                  <Box key={f.id} sx={{ mb: 1.5 }}>
                    <Typography level="title-sm">{f.label} · Page {f.page}</Typography>
                    <Typography level="body-sm">{roleLabel(f.signer)} · {f.required ? "Required" : "Optional"}</Typography>
                    {f.issues.map(issue => <Typography level="body-sm" color="danger" key={issue}>{issue}</Typography>)}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion></AccordionGroup>
            {draft && <Button component={Link} to={`/crm/contracts/${contract.id}/design`} variant="outlined">Edit signing fields</Button>}
            {data?.readiness.ready && (
              <Alert color="success">
                Fields and recipient assignments are ready for review.
              </Alert>
            )}
            {draft && (
              <>
                <Button
                  component={Link}
                  to={`/crm/contracts/${contract.id}/sign-in-person`}
                  variant="outlined"
                  disabled={!data?.readiness.ready || recipientsDirty || !!busy || workflow.isFetching}
                >
                  Sign in person
                </Button>
              </>
            )}
          </Card>
          <AccordionGroup><Accordion><AccordionSummary>Signing order & progress</AccordionSummary><AccordionDetails>
          <SigningOrderManager
            key={`${contract.id}-${JSON.stringify(contract.recipients)}-${contract.use_signing_order}`}
            contractId={contract.id}
            contractStatus={contract.status}
            initialUseSigningOrder={contract.use_signing_order}
            initialRecipients={contract.recipients}
            isReadOnly={!draft || recipientsDirty}
            onUpdate={refresh}
          />
          </AccordionDetails></Accordion></AccordionGroup>
        </Stack>
      </Box>
      <Card sx={{ mt: 3 }}>
        <Typography level="title-lg">Manage contract</Typography>
        <Typography level="body-sm">
          {contract.is_archived ? "This contract is archived. Restore it to the active list." : "Archive to hide this contract from the active list. You can restore it later."}
          {" "}Delete permanently removes the entire contract and its signing history.
        </Typography>
        {recipientsDirty && <Typography level="body-sm">Save or cancel your recipient changes before managing this contract.</Typography>}
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button variant="outlined" disabled={!!busy || recipientsDirty} onClick={() => {
            setManagementError(""); setManagement(contract.is_archived ? "unarchive" : "archive");
          }}>{contract.is_archived ? "Restore contract" : "Archive contract"}</Button>
          <Button variant="outlined" color="danger" disabled={!!busy || recipientsDirty} onClick={() => {
            setManagementError(""); setManagement("delete");
          }}>Delete contract</Button>
        </Stack>
      </Card>
      <Modal open={management !== null} onClose={() => { if (!busy) setManagement(null); }}>
        <ModalDialog role="alertdialog" aria-labelledby="contract-management-title" sx={{ maxWidth: 540 }}>
          <DialogTitle id="contract-management-title">
            {management === "delete" ? "Delete entire contract?" : management === "archive" ? "Archive contract?" : "Restore contract?"}
          </DialogTitle>
          <DialogContent>
            <Typography level="title-md">{contract.contract_number} · {contract.title}</Typography>
            <Typography>{management === "delete"
              ? "This permanently deletes this contract, its versions, signatures and activity history. Signing links will stop working. Linked customers, proposals, jobs and separately saved documents remain. Download any contract records you need first. This cannot be undone."
              : management === "archive"
                ? "Hide this contract from the active list. Its documents and signing history remain available, and you can restore it from the Archived filter. Archiving does not cancel existing signing invitations."
                : "Return this contract to the active list with its documents and signing history unchanged."}</Typography>
            {managementError && <Alert color="danger">{managementError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button color={management === "delete" ? "danger" : "primary"} loading={busy === "management"} onClick={async () => {
              setBusy("management"); setManagementError("");
              try {
                if (management === "delete") {
                  await api.delete(`/api/crm/contracts/${contract.id}/?updated_at=${encodeURIComponent(contract.updated_at)}`);
                  await client.invalidateQueries({ queryKey: ["contract-list"] });
                  await client.invalidateQueries({ queryKey: ["contract-report"] });
                  client.removeQueries({ queryKey: ["contract-detail", id] });
                  client.removeQueries({ queryKey: ["contract-workflow", id] });
                  navigate("/crm/contracts");
                } else {
                  await api.post(`/api/crm/contracts/${contract.id}/${management}/`, {});
                  await refresh();
                  await client.invalidateQueries({ queryKey: ["contract-list"] });
                  await client.invalidateQueries({ queryKey: ["contract-report"] });
                }
                setManagement(null);
              } catch (e) {
                const failure = e as { responseData?: { error?: string }; message?: string };
                setManagementError(failure?.responseData?.error || failure?.message || "Action failed. Please try again.");
              } finally { setBusy(""); }
            }}>{management === "delete" ? "Permanently delete contract" : management === "archive" ? "Confirm archive" : "Confirm restore"}</Button>
            <Button variant="plain" disabled={!!busy} onClick={() => setManagement(null)}>Cancel</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
      {pdf && (
        <PdfViewerModal
          open={preview}
          onClose={() => setPreview(false)}
          pdfUrl={pdf}
          title={contract.title}
          fields={
            contract.completed_pdf_url ? undefined : contract.contract_fields
          }
          fieldValues={contract.contract_data}
        />
      )}
      {sendOpen && draft && (
        <SendContractModal
          key={id}
          open
          onClose={() => setSendOpen(false)}
          contract={contract}
          onSend={send}
        />
      )}
    </Box>
  );
};
